import type { KagemaneResolution } from '@/components/ui/KagemaneResolutionChoice';

const KEY = 'kagemane:login-draft';
const CONSUMED_KEY = `${KEY}:consumed`;
const MAX_AGE_MS = 30 * 60 * 1000;
type Draft = { templateId: string | null; projectId: string | null; prompt: string; promptEnabled: boolean; depthEnabled?: boolean; resolution: KagemaneResolution; model?: 'seedance' | 'heygen' | 'kling4' | 'genjutsu'; genjutsuMode?: 'motion-transfer' | 'object-swap' | 'restyle'; genjutsuPresetId?: string; picker?: 'image' | 'video'; ownerId?: string | null };

export function kagemaneDraftStorage(kind: 'session' | 'local'): Storage | null {
  try { return kind === 'session' ? window.sessionStorage : window.localStorage; }
  catch { return null; }
}

/** A login round-trip must not reset an already authored instruction. */
export function saveKagemaneLoginDraft(storage: Storage | null, draft: Draft, now = Date.now(), backup?: Storage | null) {
  const raw = JSON.stringify({ ...draft, savedAt: now, draftId: `${now}-${Math.random().toString(36).slice(2)}` });
  for (const target of [storage, backup]) {
    try { target?.setItem(KEY, raw); }
    catch { /* A denied storage surface must not prevent sign-in. */ }
  }
}

export function consumeKagemaneLoginDraft(storage: Storage | null, templateId: string | null, projectId: string | null, now = Date.now(), backup?: Storage | null, ownerId?: string): Draft | null {
  let raw: string | null = null;
  for (const target of [storage, backup]) {
    try { raw ||= target?.getItem(KEY) ?? null; target?.removeItem(KEY); }
    catch { /* Try the same-browser handoff copy if session storage is denied. */ }
  }
  if (!raw) return null;
  try {
    const draft = JSON.parse(raw);
    try { if (draft.draftId && backup?.getItem(CONSUMED_KEY) === draft.draftId) return null; } catch { /* Storage may be revoked. */ }
    if (draft.templateId !== templateId || draft.projectId !== projectId
      || typeof draft.savedAt !== 'number' || now < draft.savedAt || now - draft.savedAt > MAX_AGE_MS
      || typeof draft.prompt !== 'string' || draft.prompt.length > (draft.model === 'genjutsu' ? 10000 : 6000) || typeof draft.promptEnabled !== 'boolean'
      || !['480p', '768p', '720p', '1080p', '2k', '4k'].includes(draft.resolution)) return null;
    if (draft.depthEnabled !== undefined && typeof draft.depthEnabled !== 'boolean') return null;
    if (draft.model !== undefined && !['seedance', 'heygen', 'kling4', 'genjutsu'].includes(draft.model)) return null;
    if (draft.model === 'kling4' && !['720p', '1080p', '2k', '4k'].includes(draft.resolution)) return null;
    if (draft.model === 'genjutsu' && !['480p', '720p', '1080p'].includes(draft.resolution)) return null;
    if (draft.genjutsuMode !== undefined && !['motion-transfer', 'object-swap', 'restyle'].includes(draft.genjutsuMode)) return null;
    if (draft.genjutsuPresetId !== undefined && (typeof draft.genjutsuPresetId !== 'string' || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(draft.genjutsuPresetId))) return null;
    if (draft.picker !== undefined && !['image', 'video'].includes(draft.picker)) return null;
    if (draft.ownerId && draft.ownerId !== ownerId) return null;
    try { if (draft.draftId) backup?.setItem(CONSUMED_KEY, draft.draftId); } catch { /* Restore even if a handoff marker cannot be saved. */ }
    const { draftId: _handoffId, ...restored } = draft;
    return restored;
  } catch { return null; }
}
